import React, { useEffect, useRef } from 'react';
import { FieldError, useFormField, useFormActions } from '../../core';

import { uiConfig } from '@/config';
import { CheckGroup } from '@/ui-kit';
import { cn } from '@/libs';

import { FormFieldMapper } from '..';
import { config } from '../../config';
import {
  useClearOnNotApplicable,
  useNativeInput,
  useNotApplicableField,
  useShouldValidateField,
} from '../../hooks';
import type { QuestionWithDetailsFormField } from '../../typings';
import { FormFieldAlertMessage } from './FormFieldAlertMessage';

import './QuestionWithDetailsPresenter.scss';

type QuestionWithDetailsPresenterProps = {
  field: QuestionWithDetailsFormField;
  disabled?: boolean;
};

type Answer = {
  yes: boolean;
  no: boolean;
};

const AnswerMap = {
  yes: 'true',
  no: 'false',
} as const;

export const QuestionWithDetailsPresenter: React.FC<
  QuestionWithDetailsPresenterProps
> = ({ field, disabled }) => {
  const isFieldNA = useNotApplicableField(field.notApplicableField?.fieldId);

  useClearOnNotApplicable(field, isFieldNA);
  const requiredFieldRef = useRef<boolean | undefined>(undefined);

  const [inputField] = useFormField(
    `${field.fieldId}${config.questionWithDetailsFieldSuffix}`,
  );

  // Read from Formik, not `field.answer`: a server snapshot can rebuild the field
  // with an older answer while Formik keeps the provider's. Not Applicable clears
  // it to ''.
  const questionAnswer: Answer = {
    yes: inputField.value === 'yes',
    no: inputField.value === 'no',
  };

  const { setFieldValue } = useFormActions();

  const [requiredField, requiredFieldMeta] = useFormField(
    `${field.fieldId}${config.requiredFieldSuffix}`,
  );

  const hasRequiredError =
    requiredFieldMeta.touched && !!requiredFieldMeta.error;

  const containerClasses = cn(
    'qd__main-container',
    uiConfig.grid.maxColumnsSpanClass,
  );
  const isMatchingAnswerSelected =
    field.matchingAnswer !== undefined &&
    (field.matchingAnswer ? questionAnswer.yes : questionAnswer.no);

  const { setNativeValue } = useNativeInput(
    `${field.fieldId}${config.questionWithDetailsFieldSuffix}`,
    'text',
  );

  const naFieldId = field.notApplicableField?.fieldId;

  // Not Applicable sits in the same group as the answers, so one variable decides which of the
  // three is checked. Reading N/A first also settles rows saved with both an answer and the tick.
  const selectedChoice = isFieldNA
    ? naFieldId
    : questionAnswer.yes
      ? `${field.fieldId}_yes`
      : questionAnswer.no
        ? `${field.fieldId}_no`
        : undefined;

  const statusOf = (key: string) =>
    selectedChoice === key ? ('checked' as const) : ('unchecked' as const);

  // Re-synced on drift: a snapshot can reseed it while autosave keeps the provider's answer.
  useShouldValidateField(
    field.fieldId,
    !isFieldNA && (questionAnswer.yes || questionAnswer.no),
  );

  useEffect(() => {
    const newValue: boolean =
      (inputField.value !== '' &&
        inputField.value !== undefined &&
        field.isRequired) ||
      isFieldNA ||
      !field.isRequired;

    if (
      requiredFieldRef.current !== newValue ||
      requiredField.value !== newValue
    ) {
      requiredFieldRef.current = newValue;
      setFieldValue(
        `${field.fieldId}${config.requiredFieldSuffix}`,
        newValue,
      );
    }
  }, [
    field.fieldId,
    inputField,
    setFieldValue,
    isFieldNA,
    requiredField,
    field.isRequired,
  ]);

  return (
    <div id={field.fieldId} className={containerClasses}>
      {field.alertMessage && (
        <FormFieldAlertMessage alertMessage={field.alertMessage} />
      )}
      <p className="qd__question">
        {field.question}
        {field.isRequired && (
          <span style={{ color: 'var(--color_error)' }}> *</span>
        )}
      </p>
      <input
        type="text"
        {...inputField}
        value={inputField.value as string | undefined}
        hidden
      />
      <CheckGroup
        orientation="column"
        size="large"
        group={field.fieldId}
        error={hasRequiredError}
        choices={[
          ...(['yes', 'no'] as const).map(answer => ({
            key: `${field.fieldId}_${answer}`,
            value: field[`${AnswerMap[answer]}OptionText`],
            status: statusOf(`${field.fieldId}_${answer}`),
            disabled: disabled ?? false,
          })),
          ...(naFieldId !== undefined && field.notApplicableField !== undefined
            ? [
                {
                  key: naFieldId,
                  value: field.notApplicableField.label,
                  status: statusOf(naFieldId),
                  disabled: disabled ?? false,
                },
              ]
            : []),
        ]}
        onOptionChange={c => {
          if (naFieldId !== undefined && c.key === naFieldId) {
            // Turning it on clears the answer through useClearOnNotApplicable.
            setFieldValue(naFieldId, !isFieldNA);
            return;
          }

          const isYes = c.key === `${field.fieldId}_yes`;

          if (isFieldNA && naFieldId !== undefined) {
            // Answering takes the tick off. Formik holds the N/A key ahead of the answer's, so the
            // batch sends the N/A update first — the server refuses a value written while N/A is on.
            setFieldValue(naFieldId, false);
          } else if (selectedChoice === c.key) {
            setNativeValue('');
            return;
          }

          setNativeValue(isYes ? 'yes' : 'no');
        }}
      />

      <FieldError name={`${field.fieldId}${config.requiredFieldSuffix}`} />

      {isFieldNA && field.naDetails && (
        <FormFieldMapper field={field.naDetails} />
      )}

      {!isFieldNA && !disabled && questionAnswer.no && field.falseDetails && (
        <FormFieldMapper field={field.falseDetails} />
      )}

      {!isFieldNA && !disabled && questionAnswer.yes && field.trueDetails && (
        <FormFieldMapper field={field.trueDetails} />
      )}

      {!isFieldNA &&
        !disabled &&
        isMatchingAnswerSelected &&
        field.matchingDetails && <FormFieldMapper field={field.matchingDetails} />}
    </div>
  );
};
